import { debounce } from '@cian/ui-kit/internal/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { ERuntimeSettings } from 'shared/common/types/runtimeSettings';
import { useApplicationContext } from 'shared/common/utils/applicationContext';
import { useTracking } from 'shared/mortgageMiniCalculator/utils/tracking/useTracking';
import { EFieldName } from '../../../constants';
import { IFetchOffersProps } from '../../../hooks/useGetOffers';
import { selectFormDefaults } from '../../../selectors/calculator';
import { IFormValues, TBenefitProgram } from '../../../types';
import { calculateValueFromPercentage, getDownPaymentFilterValue } from '../../../utils';
import { mapFieldNameToAnalyticsFieldName } from '../../../utils/mapFieldNameToAnalyticsFieldName';
import { ETypeChangeInput } from '../../../utils/tracking/types';
import { DEFAULT_DOWN_PAYMENT_PERCENTAGE, DOWN_PAYMENT_VALUES } from '../constants';

export const useFormCalculator = (fetchOffers: (params: IFetchOffersProps) => void) => {
  const initialFormValues = useSelector(selectFormDefaults);
  const { trackEditFieldValues } = useTracking();

  const [percentDownPayment, setPercentDownPayment] = useState(() => {
    return (initialFormValues.downPayment / initialFormValues.objectCost) * 100 || DEFAULT_DOWN_PAYMENT_PERCENTAGE;
  });

  const { config } = useApplicationContext();
  const isProgramFilterFieldEnabled = config.get<boolean>(ERuntimeSettings.programFilterFieldEnabled) ?? false;

  const {
    setValue,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<IFormValues>({
    defaultValues: initialFormValues,
  });

  const { downPayment, objectCost, loanTermYears, benefitProgram } = watch();

  const onSubmit = useCallback(
    async (data: IFormValues) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { benefitProgram, ...restData } = data;
      const submitData = isProgramFilterFieldEnabled ? data : restData;
      fetchOffers(submitData);
    },
    [fetchOffers, isProgramFilterFieldEnabled],
  );

  const downPaymentOptions = useMemo(() => {
    return DOWN_PAYMENT_VALUES.map(option => {
      const filterValue = getDownPaymentFilterValue(option);

      return {
        value: calculateValueFromPercentage(objectCost, filterValue),
        label: `${option}%`,
        dataTestId: `down-payment-${option}`,
      };
    });
  }, [objectCost]);

  const downPaymentChipValue = useMemo(() => {
    if (!objectCost || !downPayment) {
      return '';
    }

    // Проверяем, что процент от текущего downPayment попадает в диапазон,
    // например, от 20 до 20.1, чтобы включить предложения с неровными процентами
    const currentPercentage = (downPayment / objectCost) * 100;

    for (const option of DOWN_PAYMENT_VALUES) {
      const optionNumber = Number(option);
      const filterValue = getDownPaymentFilterValue(option);

      // 0.001 на случай ошибки плавующей запятой
      if (currentPercentage >= optionNumber && currentPercentage <= filterValue + 0.001) {
        // Возвращаем значение, рассчитанное по filterValue для консистентности
        return calculateValueFromPercentage(objectCost, filterValue);
      }
    }

    return '';
  }, [downPayment, objectCost]);

  useEffect(() => {
    setValue(EFieldName.ObjectCost, initialFormValues.objectCost);
    setValue(EFieldName.DownPayment, initialFormValues.downPayment);
    setValue(EFieldName.loanTermYears, initialFormValues.loanTermYears);
    if (isProgramFilterFieldEnabled && initialFormValues.benefitProgram !== undefined) {
      setValue(EFieldName.benefitProgram, initialFormValues.benefitProgram);
    }
  }, [initialFormValues, setValue, isProgramFilterFieldEnabled]);

  useEffect(() => {
    if (initialFormValues.downPayment && initialFormValues.objectCost) {
      const newPercent = (initialFormValues.downPayment / initialFormValues.objectCost) * 100;
      setPercentDownPayment(newPercent);
    }
  }, [initialFormValues.downPayment, initialFormValues.objectCost]);

  useEffect(() => {
    // Пересчитываем downPayment только если objectCost изменился
    // Не пересчитываем если текущее значение уже соответствует одному из процентов из опций
    const calculatedValue = calculateValueFromPercentage(objectCost, percentDownPayment);

    // Если пересчитанное значение совпадает с текущим (с допуском 1), не пересчитываем
    if (Math.abs(calculatedValue - downPayment) <= 1) {
      return;
    }

    // Проверяем, соответствует ли текущее значение одному из процентов из опций
    // Используем диапазон [option, filterValue] для проверки
    let matchesOption = false;
    if (objectCost > 0) {
      const currentPercentage = (downPayment / objectCost) * 100;
      // Добавляем небольшой допуск для погрешности вычислений с плавающей точкой
      const EPSILON = 0.001;
      for (const option of DOWN_PAYMENT_VALUES) {
        const optionNumber = Number(option);
        const filterValue = getDownPaymentFilterValue(option);
        if (currentPercentage >= optionNumber && currentPercentage <= filterValue + EPSILON) {
          matchesOption = true;
          break;
        }
      }
    }

    // Не пересчитываем если текущее значение соответствует одной из опций
    if (matchesOption) {
      return;
    }

    // Пересчитываем только если значение значительно отличается
    setValue(EFieldName.DownPayment, calculatedValue);
  }, [objectCost, percentDownPayment, setValue, downPayment]);

  const { debouncedSubmit, debouncedTrack } = useMemo(() => {
    return {
      debouncedSubmit: debounce(handleSubmit(onSubmit), 500),
      debouncedTrack: debounce(trackEditFieldValues, 500),
    };
  }, [handleSubmit, onSubmit, trackEditFieldValues]);

  const getHandlerByFieldName = useCallback(
    (fieldName: EFieldName, typeChange: ETypeChangeInput) => async (value: number | string) => {
      if (fieldName === EFieldName.benefitProgram) {
        setValue(fieldName, value as TBenefitProgram);
      } else if (fieldName === EFieldName.DownPayment && typeChange === ETypeChangeInput.AUTO) {
        // При клике на чипс value - это числовое значение, вычисленное из процента
        const calculatedValue = value as number;
        const percentValue = (calculatedValue / objectCost) * 100;
        setPercentDownPayment(percentValue);
        setValue(fieldName, calculatedValue);
      } else {
        setValue(fieldName, value as number);
      }

      if (fieldName === EFieldName.DownPayment && typeChange === ETypeChangeInput.MANUAL) {
        setPercentDownPayment(((value as number) / objectCost) * 100);
      }

      const trackingFieldName = mapFieldNameToAnalyticsFieldName[fieldName];

      if (isValid) {
        await debouncedSubmit();
        debouncedTrack(typeChange, value, trackingFieldName);
      }
    },
    [setValue, objectCost, isValid, debouncedSubmit, debouncedTrack],
  );

  return {
    objectCost,
    downPayment,
    loanTermYears,
    downPaymentOptions,
    downPaymentChipValue,
    getHandlerByFieldName,
    isValid,
    benefitProgram,
  };
};
